/**
 * DiagramEditor — Iter Feb 2026 R39.
 *
 * Full-canvas React Flow editor with:
 *   • Left palette (drag / click to add shape)
 *   • Top toolbar (undo / redo, save, share, scope, revisions, export)
 *   • Right inspector (label, color, shape, link-to-entity, comments)
 *   • Auto-save every 3s when dirty
 *
 * The wire format matches the backend model: nodes[], edges[], viewport.
 * Node data may carry `shape` ("start"|"process"|"decision"|"data"|"end"|"note")
 * which controls the visual chrome via NodeShell.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ReactFlow, Background, Controls, MiniMap, Handle, Position,
  applyNodeChanges, applyEdgeChanges, addEdge, ReactFlowProvider,
  useReactFlow, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { toPng } from "html-to-image";
import dagre from "dagre";
import api from "@/lib/api";
import { API as API_BASE } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Save, Undo2, Redo2, Share2, Download, History, Link2, Trash2,
  Circle, Square, Diamond, Database, StopCircle, StickyNote, MessageSquare,
  Sparkles, Lock, Globe2, Users as UsersIcon, Building2, Package as PackageIcon, ExternalLink, Copy,
  Zap, PlayCircle, PauseCircle, LayoutGrid as TidyIcon,
  Maximize2, Minimize2, Eye, Presentation, ChevronLeft, ChevronRight,
  Cylinder, Hexagon, Cloud, Wand2, Paperclip, FileText, X, Upload, Image as ImageIcon,
} from "lucide-react";
import { CustomNode, CUSTOM_SHAPES, EDGE_TYPES, ARROW_STYLES } from "@/components/diagrams/CustomNode";
import { RichTextEditor } from "@/components/diagrams/RichTextEditor";
import { AskAIDialog } from "@/components/diagrams/AskAIDialog";
import { NodeCommentThread } from "@/components/diagrams/NodeCommentThread";
import ImageLightbox from "@/components/diagrams/ImageLightbox";

// R45 · Dagre auto-layout — spreads nodes left→right with clean spacing
function tidyLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 60, ranksep: 80 });
  nodes.forEach(n => g.setNode(n.id, { width: n.width || 160, height: n.height || 60 }));
  edges.forEach(e => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map(n => {
    const p = g.node(n.id);
    return p ? { ...n, position: { x: p.x - (n.width || 160) / 2, y: p.y - (n.height || 60) / 2 } } : n;
  });
}

const FLOW_EVENTS = [
  "lead.created", "lead.updated", "lead.stage_changed", "lead.won", "lead.lost",
  "meta.lead.submitted", "campaign.anomaly", "task.created", "task.completed",
];
const FLOW_ACTIONS = [
  { key: "webhook.post",     label: "HTTP POST to URL" },
  { key: "task.create",      label: "Create task" },
  { key: "tag.add",          label: "Add tag to lead" },
  { key: "lead.assign",      label: "Assign lead to user" },
  { key: "lead.stage_set",   label: "Move lead to stage" },
  { key: "wa.send_template", label: "Send WA template (stub)" },
];

const SHAPES = CUSTOM_SHAPES.map(s => {
  const iconMap = {
    start: Circle, end: StopCircle, process: Square, decision: Diamond,
    data: Database, note: StickyNote, cylinder: Cylinder, hexagon: Hexagon,
    parallelogram: Square, cloud: Cloud,
  };
  const typeMap = { start: "input", end: "output" };
  return { key: s.key, label: s.label, icon: iconMap[s.key] || Square, nodeType: typeMap[s.key] || "default" };
});

const DEFAULT_COLOR = "#0ea5e9";
const SCOPE_ICONS = {
  private: Lock, workspace: Globe2, client: UsersIcon, brand: PackageIcon, project: Building2,
};

// ─────────────── Custom node visual (imported from components/) ───────────────
const nodeTypes = { flow: CustomNode };

function toRF(n) {
  return { ...n, type: "flow" };
}

// ─────────────── Editor ───────────────

export default function DiagramEditor() {
  return (
    <ReactFlowProvider>
      <EditorInner />
    </ReactFlowProvider>
  );
}

function EditorInner() {
  const { activeWorkspaceId } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const rf = useReactFlow();
  const canvasRef = useRef(null);

  const [diagram, setDiagram] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState({ past: [], future: [] });
  const [showRevisions, setShowRevisions] = useState(false);
  const [shareInfo, setShareInfo] = useState(null); // { token, url }
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  // R45 slice 2 — view/edit modes
  const [viewMode, setViewMode] = useState("advanced"); // "advanced" | "minimal"
  const [fullscreen, setFullscreen] = useState(false);
  const [presentation, setPresentation] = useState(false);
  const [askAI, setAskAI] = useState(false);
  const [members, setMembers] = useState([]); // for @mentions + responsible picker
  const [lightbox, setLightbox] = useState(null); // R47 · { src, alt } | null
  const [canvasDropHint, setCanvasDropHint] = useState(false); // R47 · UI hint during drag-over

  // ── Load ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/diagrams/${id}`);
        if (cancel) return;
        setDiagram(data);
        setNodes((data.nodes || []).map(toRF));
        setEdges(data.edges || []);
        if (data.public_share_enabled && data.public_token) {
          setShareInfo({
            token: data.public_token,
            url: `${window.location.origin}/diagram/${data.public_token}`,
          });
        }
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load diagram")); }
    })();
    return () => { cancel = true; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/diagrams/${id}/comments`).then(r => setComments(r.data?.items || [])).catch(() => {});
  }, [id]);

  // R45 slice 2 · workspace members for @mentions + responsible picker
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`).then(r => {
      const arr = (r.data?.items || r.data || []).map(m => ({
        id: m.user_id || m.id, name: m.name || m.full_name, email: m.email,
      })).filter(u => u.id);
      setMembers(arr);
    }).catch(() => {});
  }, [activeWorkspaceId]);

  // Comment counts per node (memoised)
  const commentCounts = useMemo(() => {
    const m = {};
    (comments || []).forEach(c => {
      const k = c.node_id || "__diagram__";
      if (c.resolved) return; // only unresolved show in badge
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [comments]);

  // Displayed nodes = nodes with viewMode + commentCount + responsible_name injected
  const openLightbox = useCallback((src, alt) => setLightbox({ src, alt }), []);
  const displayNodes = useMemo(() => {
    const memberById = new Map(members.map(u => [u.id, u]));
    return nodes.map(n => {
      const rid = n.data?.responsible_user_id;
      const rmem = rid ? memberById.get(rid) : null;
      return {
        ...n,
        draggable: !presentation,
        selectable: !presentation,
        data: {
          ...n.data,
          viewMode,
          commentCount: commentCounts[n.id] || 0,
          responsible_name: rmem?.name || rmem?.email || n.data?.responsible_name,
          onImageClick: openLightbox,
        },
      };
    });
  }, [nodes, viewMode, commentCounts, members, presentation, openLightbox]);

  // ── Auto-save ───────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!diagram) return;
    setSaving(true);
    try {
      const payload = {
        nodes: nodes.map(n => {
          // strip UI-only fields injected by displayNodes
          const {
            viewMode: _vm, commentCount: _cc, responsible_name: _rn,
            onImageClick: _oic, // R47 · UI-only handler
            ...cleanData
          } = (n.data || {});
          return {
            id: n.id, type: n.type, position: n.position, data: cleanData,
            width: n.width, height: n.height,
          };
        }),
        edges: edges.map(e => ({
          id: e.id, source: e.source, target: e.target,
          sourceHandle: e.sourceHandle, targetHandle: e.targetHandle,
          label: e.label, type: e.type, animated: e.animated,
          style: e.style, data: e.data, markerEnd: e.markerEnd,
        })),
        viewport: rf.getViewport ? rf.getViewport() : undefined,
      };
      const { data } = await api.patch(`/diagrams/${id}`, payload);
      setDiagram(data);
      setDirty(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  }, [id, nodes, edges, diagram, rf]);

  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => save(), 3000);
    return () => clearTimeout(t);
  }, [dirty, save]);

  // ── History (undo/redo) ─────────────────────────────────────────────
  const snapshot = useCallback(() => {
    setHistory(h => ({
      past: [...h.past.slice(-30), { nodes, edges }],
      future: [],
    }));
  }, [nodes, edges]);

  const undo = () => {
    setHistory(h => {
      if (!h.past.length) return h;
      const prev = h.past[h.past.length - 1];
      setNodes(prev.nodes);
      setEdges(prev.edges);
      setDirty(true);
      return { past: h.past.slice(0, -1), future: [{ nodes, edges }, ...h.future] };
    });
  };
  const redo = () => {
    setHistory(h => {
      if (!h.future.length) return h;
      const next = h.future[0];
      setNodes(next.nodes);
      setEdges(next.edges);
      setDirty(true);
      return { past: [...h.past, { nodes, edges }], future: h.future.slice(1) };
    });
  };

  // ── React Flow callbacks ────────────────────────────────────────────
  const onNodesChange = useCallback((changes) => {
    setNodes(nds => applyNodeChanges(changes, nds));
    if (changes.some(c => c.type !== "select" && c.type !== "dimensions")) setDirty(true);
  }, []);
  const onEdgesChange = useCallback((changes) => {
    setEdges(eds => applyEdgeChanges(changes, eds));
    if (changes.some(c => c.type !== "select")) setDirty(true);
  }, []);
  const onConnect = useCallback((conn) => {
    snapshot();
    const edgeType = diagram?.default_edge_type || "smoothstep";
    const arrow = diagram?.default_arrow || "arrowclosed";
    setEdges(eds => addEdge({
      ...conn,
      id: `e${Date.now()}`,
      type: edgeType,
      markerEnd: arrow === "none" ? undefined : { type: arrow === "arrow" ? MarkerType.Arrow : MarkerType.ArrowClosed },
    }, eds));
    setDirty(true);
  }, [snapshot, diagram]);
  const onSelectionChange = useCallback((sel) => {
    const n = sel?.nodes?.[0];
    setSelected(n ? { kind: "node", id: n.id } : null);
  }, []);

  // R45 · Double-click to inline-edit label
  const onNodeDoubleClick = useCallback((_e, node) => {
    const next = window.prompt("Edit label:", node.data?.label || "");
    if (next !== null && next !== node.data?.label) {
      snapshot();
      setNodes(nds => nds.map(n => n.id === node.id ? { ...n, data: { ...n.data, label: next } } : n));
      setDirty(true);
    }
  }, [snapshot]);

  // ── Add shape ───────────────────────────────────────────────────────
  const addShape = (shape) => {
    snapshot();
    const cx = 200 + Math.random() * 200;
    const cy = 200 + Math.random() * 200;
    const node = {
      id: `n${Date.now()}`,
      type: "flow",
      position: { x: cx, y: cy },
      data: { label: SHAPES.find(s => s.key === shape)?.label || "Node", shape, color: DEFAULT_COLOR },
    };
    setNodes(nds => [...nds, node]);
    setSelected({ kind: "node", id: node.id });
    setDirty(true);
  };

  // ── Selected node updates ───────────────────────────────────────────
  const selNode = useMemo(
    () => (selected?.kind === "node" ? nodes.find(n => n.id === selected.id) : null),
    [selected, nodes],
  );
  const updateSelected = (patch) => {
    if (!selNode) return;
    setNodes(nds => nds.map(n => n.id === selNode.id ? { ...n, data: { ...n.data, ...patch } } : n));
    setDirty(true);
  };
  const deleteSelected = () => {
    if (!selNode) return;
    snapshot();
    setNodes(nds => nds.filter(n => n.id !== selNode.id));
    setEdges(eds => eds.filter(e => e.source !== selNode.id && e.target !== selNode.id));
    setSelected(null);
    setDirty(true);
  };

  // ── Export ──────────────────────────────────────────────────────────
  const exportJson = () => {
    const blob = new Blob([JSON.stringify({
      title: diagram?.title, nodes, edges,
    }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(diagram?.title || "diagram").replace(/[^a-z0-9\-_]/gi, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportPng = async () => {
    const el = document.querySelector(".react-flow__renderer");
    if (!el) return toast.error("Canvas not ready");
    try {
      const png = await toPng(el, { pixelRatio: 2, backgroundColor: "#fff" });
      const a = document.createElement("a");
      a.href = png;
      a.download = `${(diagram?.title || "diagram").replace(/[^a-z0-9\-_]/gi, "_")}.png`;
      a.click();
    } catch (e) { toast.error("PNG export failed"); }
  };

  // ── Share ───────────────────────────────────────────────────────────
  const enableShare = async () => {
    try {
      const { data } = await api.post(`/diagrams/${id}/share`);
      const url = `${window.location.origin}/diagram/${data.token}`;
      setShareInfo({ token: data.token, url });
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Public link copied");
    } catch (e) { toast.error(apiErrorMessage(e, "Share failed")); }
  };
  const disableShare = async () => {
    try {
      await api.delete(`/diagrams/${id}/share`);
      setShareInfo(null);
      toast.success("Public link revoked");
    } catch (e) { toast.error(apiErrorMessage(e, "Revoke failed")); }
  };

  // ── Scope / title changes ───────────────────────────────────────────
  const updateMeta = async (patch) => {
    try {
      const { data } = await api.patch(`/diagrams/${id}`, patch);
      setDiagram(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  // R47 · Canvas drag-drop: drop an image file over a node → upload + attach.
  // Falls back to attaching to the selected node if no node is under cursor.
  const onCanvasDragOver = useCallback((e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault(); e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    if (!canvasDropHint) setCanvasDropHint(true);
  }, [canvasDropHint]);

  const onCanvasDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setCanvasDropHint(false);
  }, []);

  const onCanvasDrop = useCallback(async (e) => {
    setCanvasDropHint(false);
    const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type?.startsWith("image/"));
    if (!files.length) return;
    e.preventDefault(); e.stopPropagation();
    // Find the DOM node directly under the cursor and match it against
    // React Flow node ids so users can drop an image right onto a shape.
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const nodeEl = el?.closest?.("[data-id]");
    const targetId = nodeEl?.getAttribute("data-id") || selNode?.id;
    if (!targetId) {
      toast.error("Drop the image onto a node (or select one first)");
      return;
    }
    if (!activeWorkspaceId) { toast.error("Workspace not ready"); return; }
    try {
      const target = nodes.find(n => n.id === targetId);
      const existing = target?.data?.attachments || [];
      const uploaded = [];
      for (const f of files) {
        if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name} > 25MB`); continue; }
        const up = await uploadDiagramImage(activeWorkspaceId, f);
        uploaded.push({
          kind: "image", url: up.url, file_id: up.file_id,
          label: up.original_filename || f.name,
          display: (uploaded.length === 0 && !existing.some(a => a.kind === "image" && (a.display === "fill" || a.display === "below")))
            ? "fill" : "badge",
        });
      }
      if (!uploaded.length) return;
      snapshot();
      setNodes(nds => nds.map(n =>
        n.id === targetId
          ? { ...n, data: { ...n.data, attachments: [...(n.data?.attachments || []), ...uploaded] } }
          : n
      ));
      setDirty(true);
      toast.success(`Attached ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Image upload failed"));
    }
  }, [selNode, activeWorkspaceId, nodes, snapshot]);

  if (!diagram) {
    return <div className="p-6 text-sm text-muted-foreground">Loading diagram…</div>;
  }

  const ScopeIcon = SCOPE_ICONS[diagram.scope] || Lock;

  const shellClass = fullscreen
    ? "fixed inset-0 z-[80] bg-background flex flex-col"
    : "flex flex-col h-full";

  return (
    <div className={shellClass} data-testid="diagram-editor">
      {/* Top toolbar */}
      <div className="border-b bg-background flex items-center gap-2 px-3 py-2 flex-wrap">
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => fullscreen ? setFullscreen(false) : navigate("/app/diagrams")} data-testid="diagram-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> {fullscreen ? "Exit fullscreen" : "Back"}
        </Button>
        <div className="border-l h-6 mx-1" />
        <Input
          value={diagram.title || ""}
          onChange={e => setDiagram(d => ({ ...d, title: e.target.value }))}
          onBlur={e => updateMeta({ title: e.target.value })}
          className="h-8 rounded-none w-60 font-semibold"
          data-testid="diagram-title-input"
        />
        <div className="flex items-center gap-1 border h-8 px-2">
          <ScopeIcon className="w-3.5 h-3.5" />
          <Select value={diagram.scope} onValueChange={(v) => { setDiagram(d => ({ ...d, scope: v })); updateMeta({ scope: v }); }}>
            <SelectTrigger className="h-7 border-0 shadow-none rounded-none px-1 text-xs w-28" data-testid="diagram-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="private">Private</SelectItem>
              <SelectItem value="workspace">Workspace</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="brand">Brand</SelectItem>
              <SelectItem value="project">Project</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={undo} disabled={!history.past.length} data-testid="diagram-undo-btn">
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={redo} disabled={!history.future.length} data-testid="diagram-redo-btn">
          <Redo2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => setShowRevisions(true)} data-testid="diagram-history-btn">
          <History className="w-4 h-4 mr-1" /> Versions
        </Button>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => setShowComments(v => !v)} data-testid="diagram-comments-btn">
          <MessageSquare className="w-4 h-4 mr-1" /> Comments{comments.length ? ` · ${comments.length}` : ""}
        </Button>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={exportPng} data-testid="diagram-export-png-btn">
          <Download className="w-4 h-4 mr-1" /> PNG
        </Button>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => { snapshot(); setNodes(ns => tidyLayout(ns, edges)); setDirty(true); toast.success("Layout tidied"); }} data-testid="diagram-tidy-btn">
          <TidyIcon className="w-4 h-4 mr-1" /> Tidy
        </Button>
        {/* R45 slice 2 — view mode toggle */}
        <div className="flex items-center border h-8 px-1 gap-1" title="View mode">
          <Eye className="w-3.5 h-3.5" />
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="h-7 border-0 shadow-none rounded-none px-1 text-xs w-24" data-testid="diagram-view-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="advanced">Advanced</SelectItem>
              <SelectItem value="minimal">Minimal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Default edge type/arrow (applied to new connections) */}
        <div className="flex items-center border h-8 px-1 gap-1" title="Default line style">
          <Select value={diagram?.default_edge_type || "smoothstep"} onValueChange={(v) => { setDiagram(d => ({ ...d, default_edge_type: v })); updateMeta({ default_edge_type: v }); }}>
            <SelectTrigger className="h-7 border-0 shadow-none rounded-none px-1 text-xs w-24" data-testid="diagram-edge-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EDGE_TYPES.map(e => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={diagram?.default_arrow || "arrowclosed"} onValueChange={(v) => { setDiagram(d => ({ ...d, default_arrow: v })); updateMeta({ default_arrow: v }); }}>
            <SelectTrigger className="h-7 border-0 shadow-none rounded-none px-1 text-xs w-24" data-testid="diagram-arrow-style"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ARROW_STYLES.map(a => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => setFullscreen(f => !f)} data-testid="diagram-fullscreen-btn" title={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
          {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </Button>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => setPresentation(p => !p)} data-testid="diagram-presentation-btn" title="Presentation mode">
          <Presentation className={`w-4 h-4 ${presentation ? "text-emerald-600" : ""}`} />
        </Button>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => setAskAI(true)} disabled={!selNode} data-testid="diagram-ask-ai-btn" title={selNode ? "Ask AI from selected node" : "Select a node first"}>
          <Wand2 className="w-4 h-4 mr-1 text-amber-500" /> Ask AI
        </Button>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={exportJson} data-testid="diagram-export-json-btn">
          <Download className="w-4 h-4 mr-1" /> JSON
        </Button>
        <FlowPublishButton diagram={diagram} setDiagram={setDiagram} />
        {shareInfo ? (
          <>
            <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => { navigator.clipboard?.writeText(shareInfo.url); toast.success("Link copied"); }} data-testid="diagram-copy-link-btn">
              <Copy className="w-4 h-4 mr-1" /> Copy link
            </Button>
            <Button variant="ghost" size="sm" className="rounded-none h-8 text-rose-600" onClick={disableShare} data-testid="diagram-revoke-share-btn">
              Revoke
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={enableShare} data-testid="diagram-share-btn">
            <Share2 className="w-4 h-4 mr-1" /> Share
          </Button>
        )}
        <div className="flex-1" />
        <div className="text-xs text-muted-foreground">
          {saving ? "Saving…" : (dirty ? "Unsaved changes" : `v${diagram.version} · saved`)}
        </div>
        <Button size="sm" className="rounded-none h-8" onClick={save} disabled={saving} data-testid="diagram-save-btn">
          <Save className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>

      {/* Body: palette + canvas + inspector */}
      <div className="flex-1 flex overflow-hidden">
        {/* Palette */}
        <div className="w-40 border-r bg-neutral-50 p-2 space-y-1 overflow-auto">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-1 pb-1">Shapes</div>
          {SHAPES.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => addShape(s.key)}
                className="w-full flex items-center gap-2 border bg-white px-2 py-1.5 text-xs hover:border-foreground transition-colors"
                data-testid={`diagram-shape-${s.key}`}
              >
                <Icon className="w-3.5 h-3.5" /> {s.label}
              </button>
            );
          })}
          <div className="text-[10px] text-muted-foreground pt-2 px-1">
            Click a shape to drop it on the canvas. Drag between node edges to connect.
          </div>
        </div>

        {/* Canvas */}
        <div
          className="flex-1 relative"
          ref={canvasRef}
          onDragOver={onCanvasDragOver}
          onDragLeave={onCanvasDragLeave}
          onDrop={onCanvasDrop}
          data-testid="diagram-canvas"
        >
          <ReactFlow
            nodes={displayNodes}
            edges={edges}
            onNodesChange={presentation ? undefined : onNodesChange}
            onEdgesChange={presentation ? undefined : onEdgesChange}
            onConnect={presentation ? undefined : onConnect}
            onSelectionChange={onSelectionChange}
            onNodeDoubleClick={presentation ? undefined : onNodeDoubleClick}
            nodeTypes={nodeTypes}
            fitView
            defaultViewport={diagram.viewport || { x: 0, y: 0, zoom: 1 }}
            deleteKeyCode={presentation ? [] : ["Backspace", "Delete"]}
            nodesDraggable={!presentation}
            nodesConnectable={!presentation}
            elementsSelectable={!presentation}
          >
            <Background gap={20} size={1} color="#e5e7eb" />
            <Controls />
            <MiniMap pannable zoomable className="!bg-neutral-50 !border" />
          </ReactFlow>

          {showComments && (
            <NodeCommentThread
              diagramId={id}
              comments={comments}
              setComments={setComments}
              nodeId={selNode?.id}
              nodeLabel={selNode?.data?.label}
              onClose={() => setShowComments(false)}
              members={members}
            />
          )}
          {presentation && (
            <div className="absolute top-2 left-2 z-30 bg-neutral-900 text-white text-[11px] px-2 py-1 flex items-center gap-2 border border-neutral-700">
              <Presentation className="w-3 h-3 text-emerald-400" /> Presentation mode
              <button className="underline opacity-80" onClick={() => setPresentation(false)}>Exit</button>
            </div>
          )}
          {canvasDropHint && (
            <div className="pointer-events-none absolute inset-0 border-2 border-dashed border-sky-500 bg-sky-100/20 flex items-center justify-center z-20" data-testid="diagram-canvas-drop-hint">
              <div className="bg-white border shadow px-4 py-2 flex items-center gap-2 text-sm">
                <Upload className="w-4 h-4 text-sky-600" /> Drop image on a node to attach
              </div>
            </div>
          )}
        </div>

        {/* Inspector */}
        <div className="w-72 border-l bg-neutral-50 p-3 space-y-3 overflow-auto">
          {selNode ? (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Node</div>
              <div>
                <Label className="text-xs">Label</Label>
                <Textarea rows={2} value={selNode.data?.label || ""} onChange={e => updateSelected({ label: e.target.value })} className="rounded-none text-sm" data-testid="diagram-node-label" />
              </div>
              <div>
                <Label className="text-xs">Description (rich text)</Label>
                <RichTextEditor
                  value={selNode.data?.richText || ""}
                  onChange={(html) => updateSelected({ richText: html, description: html.replace(/<[^>]+>/g, "").slice(0, 280) })}
                  compact
                  placeholder="Add context, checklist, or notes… (renders in hover card + on the node)"
                />
              </div>
              <div>
                <Label className="text-xs">Responsible</Label>
                <Select value={selNode.data?.responsible_user_id || "none"} onValueChange={v => updateSelected({ responsible_user_id: v === "none" ? null : v })}>
                  <SelectTrigger className="h-8 rounded-none text-xs" data-testid="diagram-node-owner"><SelectValue placeholder="— unassigned —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— unassigned —</SelectItem>
                    {members.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <NodeAttachmentsEditor
                wsId={activeWorkspaceId}
                attachments={selNode.data?.attachments || []}
                onChange={(atts) => updateSelected({ attachments: atts })}
              />
              <div>
                <Label className="text-xs">Shape</Label>
                <Select value={selNode.data?.shape || "process"} onValueChange={v => updateSelected({ shape: v })}>
                  <SelectTrigger className="h-8 rounded-none" data-testid="diagram-node-shape"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SHAPES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Color</Label>
                <Input type="color" value={selNode.data?.color || DEFAULT_COLOR} onChange={e => updateSelected({ color: e.target.value })} className="h-8 rounded-none w-full p-1" data-testid="diagram-node-color" />
              </div>
              <div>
                <Label className="text-xs">Link to entity</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={selNode.data?.linked_entity?.type || "none"} onValueChange={v => updateSelected({ linked_entity: v === "none" ? null : { type: v, id: selNode.data?.linked_entity?.id || "" } })}>
                    <SelectTrigger className="h-8 rounded-none text-xs" data-testid="diagram-link-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      <SelectItem value="lead">Lead</SelectItem>
                      <SelectItem value="project">Project</SelectItem>
                      <SelectItem value="task">Task</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="brand">Brand</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Entity ID"
                    value={selNode.data?.linked_entity?.id || ""}
                    onChange={e => updateSelected({ linked_entity: { type: selNode.data?.linked_entity?.type || "task", id: e.target.value } })}
                    className="h-8 rounded-none text-xs"
                    data-testid="diagram-link-id"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Paste the object id (e.g. task UUID). Turns the node into a live process step.</p>
              </div>

              {/* R43 · Flow · trigger + action node config */}
              <div className="border-t pt-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Zap className="w-3 h-3" /> Flow role</div>
                <Select value={selNode.data?.node_kind || "none"} onValueChange={v => updateSelected({ node_kind: v === "none" ? null : v })}>
                  <SelectTrigger className="h-8 rounded-none text-xs" data-testid="flow-node-kind"><SelectValue placeholder="Not a flow node" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— not a flow node —</SelectItem>
                    <SelectItem value="trigger">Trigger (event)</SelectItem>
                    <SelectItem value="action">Action</SelectItem>
                  </SelectContent>
                </Select>
                {selNode.data?.node_kind === "trigger" && (
                  <Select value={selNode.data?.event || "lead.created"} onValueChange={v => updateSelected({ event: v })}>
                    <SelectTrigger className="h-8 rounded-none text-xs" data-testid="flow-node-event"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FLOW_EVENTS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {selNode.data?.node_kind === "action" && (
                  <>
                    <Select value={selNode.data?.action || "webhook.post"} onValueChange={v => updateSelected({ action: v })}>
                      <SelectTrigger className="h-8 rounded-none text-xs" data-testid="flow-node-action"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FLOW_ACTIONS.map(a => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FlowActionConfig selNode={selNode} updateSelected={updateSelected} />
                  </>
                )}
              </div>
              <Button variant="outline" className="rounded-none w-full text-rose-600 border-rose-300" onClick={deleteSelected} data-testid="diagram-node-delete">
                <Trash2 className="w-4 h-4 mr-1" /> Delete node
              </Button>
            </>
          ) : (
            <div className="text-xs text-muted-foreground space-y-2">
              <div className="text-[10px] uppercase tracking-widest">Diagram</div>
              <div>{diagram.description || "No description yet — describe what this workflow does inside the title / meta."}</div>
              <div className="pt-2">
                <Label className="text-xs">Description</Label>
                <Textarea
                  rows={4}
                  defaultValue={diagram.description || ""}
                  onBlur={e => updateMeta({ description: e.target.value })}
                  className="rounded-none text-sm"
                />
              </div>
              <div className="pt-2 text-[11px] flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-500" /> Select a node to edit it, or drop shapes from the left palette.
              </div>
            </div>
          )}
        </div>
      </div>

      {showRevisions && (
        <RevisionsDialog id={id} onClose={() => setShowRevisions(false)} onReverted={(v) => { setShowRevisions(false); toast.success(`Reverted to v${v}`); window.location.reload(); }} />
      )}
      {/* R47 · Lightbox for node images */}
      <ImageLightbox
        src={lightbox?.src}
        alt={lightbox?.alt}
        onClose={() => setLightbox(null)}
      />
      {askAI && selNode && (
        <AskAIDialog
          open={askAI}
          onClose={() => setAskAI(false)}
          diagramId={id}
          node={selNode}
          onApplyRewrite={({ label, description }) => {
            snapshot();
            setNodes(nds => nds.map(n => n.id === selNode.id ? {
              ...n,
              data: { ...n.data, label, description, richText: description ? `<p>${description}</p>` : n.data?.richText, ai_generated: true },
            } : n));
            setDirty(true);
          }}
          onInsertChildren={(suggestions) => {
            snapshot();
            const parent = selNode;
            const baseX = (parent.position?.x || 0) + 240;
            const startY = (parent.position?.y || 0) - ((suggestions.length - 1) * 90) / 2;
            const newNodes = suggestions.map((s, i) => ({
              id: `n${Date.now()}-${i}`,
              type: "flow",
              position: { x: baseX, y: startY + i * 100 },
              data: {
                label: s.label,
                shape: s.shape || "process",
                description: s.description,
                richText: s.description ? `<p>${s.description}</p>` : "",
                color: DEFAULT_COLOR,
                ai_generated: true,
              },
            }));
            const newEdges = newNodes.map((n, i) => ({
              id: `e${Date.now()}-${i}`,
              source: parent.id,
              target: n.id,
              type: diagram?.default_edge_type || "smoothstep",
              markerEnd: { type: MarkerType.ArrowClosed },
            }));
            setNodes(ns => [...ns, ...newNodes]);
            setEdges(es => [...es, ...newEdges]);
            setDirty(true);
          }}
        />
      )}
    </div>
  );
}

// ─────────────── Attachments editor ───────────────

// R47 · Upload helper — POSTs a file to the workspace file endpoint with
// scope="diagram_image" (so it's readable via the public diagram-files
// endpoint for shared views). Returns the absolute download URL + file id.
async function uploadDiagramImage(wsId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(
    `/workspaces/${wsId}/files/upload?scope=diagram_image`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  // Prefer the auth-less public URL so the image renders inside the editor,
  // hover-cards, and PublicDiagramView without needing to append a JWT.
  const publicUrl = `${API_BASE}/public/diagram-files/${data.id}`;
  return {
    url: publicUrl,
    file_id: data.id,
    original_filename: data.original_filename,
    size: data.size,
  };
}

function NodeAttachmentsEditor({ wsId, attachments = [], onChange }) {
  const [kind, setKind] = useState("image");
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const add = () => {
    if (!url.trim()) return;
    onChange([
      ...(attachments || []),
      { kind, url: url.trim(), label: label.trim() || url.trim(), display: kind === "image" ? "badge" : undefined },
    ]);
    setUrl(""); setLabel("");
  };

  const remove = (idx) => {
    const next = attachments.filter((_, i) => i !== idx);
    onChange(next);
  };

  const updateDisplay = (idx, display) => {
    // Only one attachment can be fill/below (primary image) at a time —
    // downgrade the others to "badge" if we set a new primary.
    const next = attachments.map((a, i) => {
      if (i === idx) return { ...a, display };
      if (display !== "badge" && a.kind === "image" && (a.display === "fill" || a.display === "below")) {
        return { ...a, display: "badge" };
      }
      return a;
    });
    onChange(next);
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    if (!wsId) { toast.error("Workspace not ready"); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        if (!f.type?.startsWith("image/")) {
          toast.error(`Skipped ${f.name} — not an image`);
          continue;
        }
        if (f.size > 25 * 1024 * 1024) {
          toast.error(`${f.name} exceeds 25 MB`);
          continue;
        }
        const up = await uploadDiagramImage(wsId, f);
        uploaded.push({
          kind: "image",
          url: up.url,
          label: up.original_filename || f.name,
          file_id: up.file_id,
          // First uploaded image auto-promotes to "fill" when the node has
          // no primary image yet — matches the P0 requirement that image
          // is displayed on the shape immediately without extra clicks.
          display: attachments.some(a => a.kind === "image" && (a.display === "fill" || a.display === "below"))
            ? "badge" : "fill",
        });
      }
      if (uploaded.length) {
        onChange([...(attachments || []), ...uploaded]);
        toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer?.files);
  };
  const onDragOver = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!dragActive) setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
  };

  return (
    <div className="border p-2 space-y-2 bg-white" data-testid="node-attachments-editor">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <Paperclip className="w-3 h-3" /> Attachments · {attachments.length}
      </div>
      {attachments.map((a, i) => (
        <div key={i} className="border p-1 text-[11px] space-y-1" data-testid={`attachment-${i}`}>
          <div className="flex items-center gap-1">
            {a.kind === "image" ? (
              <img src={a.url} alt="" className="w-6 h-6 object-cover border" />
            ) : a.kind === "sop" ? (
              <FileText className="w-3 h-3 text-sky-600" />
            ) : (
              <Paperclip className="w-3 h-3 text-muted-foreground" />
            )}
            <a href={a.url} target="_blank" rel="noreferrer" className="flex-1 truncate underline">{a.label || a.url}</a>
            <span className="text-[9px] uppercase text-muted-foreground">{a.kind}</span>
            <button className="text-rose-600" onClick={() => remove(i)} data-testid={`attachment-remove-${i}`}><X className="w-3 h-3" /></button>
          </div>
          {a.kind === "image" && (
            <div className="flex items-center gap-1 pl-7">
              <span className="text-[9px] uppercase text-muted-foreground">Display:</span>
              <Select value={a.display || "badge"} onValueChange={(v) => updateDisplay(i, v)}>
                <SelectTrigger className="h-6 rounded-none text-[10px] w-28" data-testid={`attachment-display-${i}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="badge">Badge only</SelectItem>
                  <SelectItem value="fill">Inside shape</SelectItem>
                  <SelectItem value="below">Below shape</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      ))}

      {/* R47 · Drag-drop zone + file picker for image upload */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed p-2 text-center cursor-pointer transition-colors text-[10px] ${
          dragActive ? "border-sky-500 bg-sky-50" : "border-neutral-300 hover:border-neutral-500"
        }`}
        data-testid="node-image-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="node-image-file-input"
        />
        {uploading ? (
          <span className="flex items-center justify-center gap-1 text-sky-700"><Upload className="w-3 h-3 animate-pulse" /> Uploading…</span>
        ) : (
          <span className="flex items-center justify-center gap-1 text-muted-foreground">
            <ImageIcon className="w-3 h-3" /> Drop image or click to upload
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-1">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-7 rounded-none text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="image">Image URL</SelectItem>
            <SelectItem value="video">Video URL</SelectItem>
            <SelectItem value="sop">SOP link</SelectItem>
            <SelectItem value="link">Web link</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="URL" value={url} onChange={(e) => setUrl(e.target.value)} className="h-7 rounded-none text-[11px] col-span-2" />
      </div>
      <div className="grid grid-cols-3 gap-1">
        <Input placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} className="h-7 rounded-none text-[11px] col-span-2" />
        <Button size="sm" className="rounded-none h-7 col-span-1" onClick={add} disabled={!url.trim()} data-testid="attachment-add">Add</Button>
      </div>
    </div>
  );
}

// ─────────────── Revisions dialog ───────────────

function RevisionsDialog({ id, onClose, onReverted }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/diagrams/${id}/revisions`)
      .then(r => setItems(r.data?.items || []))
      .catch(e => toast.error(apiErrorMessage(e, "Failed to load revisions")))
      .finally(() => setLoading(false));
  }, [id]);
  const revert = async (rid) => {
    try {
      const { data } = await api.post(`/diagrams/${id}/revert/${rid}`);
      onReverted(data.version);
    } catch (e) { toast.error(apiErrorMessage(e, "Revert failed")); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-background border w-full max-w-md p-4 space-y-3" onClick={e => e.stopPropagation()} data-testid="diagram-revisions-dialog">
        <div className="flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><History className="w-4 h-4" /> Version history</div>
          <button onClick={onClose} className="text-muted-foreground">✕</button>
        </div>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-sm text-muted-foreground">No prior versions yet — versions are created on each save.</div>}
        <div className="space-y-1 max-h-[50vh] overflow-auto">
          {items.map(r => (
            <div key={r.id} className="border p-2 flex items-center justify-between text-xs" data-testid={`diagram-rev-${r.id}`}>
              <div>
                <div className="font-mono">v{r.version}</div>
                <div className="text-[10px] text-muted-foreground">{r.created_at?.slice(0, 19).replace("T", " ")}</div>
              </div>
              <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => revert(r.id)}>Restore</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────── R43 · Publish-as-Flow button ───────────────

function FlowPublishButton({ diagram, setDiagram }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const isFlow = diagram.kind === "flow" && diagram.flow_enabled;
  const [event, setEvent] = useState(diagram.flow_trigger_event || "lead.created");

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/diagrams/${diagram.id}/publish-as-flow`, {
        event, enabled: true,
      });
      setDiagram(d => ({ ...d, ...data, kind: "flow" }));
      toast.success(`Flow live · trigger ${event}`);
      setOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Publish failed")); }
    finally { setBusy(false); }
  };
  const unpublish = async () => {
    if (!window.confirm("Unpublish this flow? It will stop running on new events.")) return;
    setBusy(true);
    try {
      await api.delete(`/diagrams/${diagram.id}/publish-as-flow`);
      setDiagram(d => ({ ...d, kind: "diagram", flow_enabled: false }));
      toast.success("Flow paused");
    } catch (e) { toast.error(apiErrorMessage(e, "Unpublish failed")); }
    finally { setBusy(false); }
  };

  if (isFlow) {
    return (
      <Button size="sm" variant="outline" className="rounded-none h-8 text-emerald-700 border-emerald-400"
                onClick={unpublish} disabled={busy} data-testid="flow-unpublish-btn">
        <Zap className="w-4 h-4 mr-1" /> Flow live · {diagram.flow_trigger_event}
      </Button>
    );
  }
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setOpen(true)} data-testid="flow-publish-btn">
        <Zap className="w-4 h-4 mr-1" /> Publish as Flow
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => !busy && setOpen(false)}>
          <div className="bg-background border w-full max-w-sm p-4 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> Publish as Flow</div>
            <div className="text-xs text-muted-foreground">
              This diagram will start running when the selected event fires. Add trigger + action nodes via the inspector on the right.
            </div>
            <Label className="text-xs">Trigger event</Label>
            <Select value={event} onValueChange={setEvent}>
              <SelectTrigger className="h-9 rounded-none" data-testid="flow-publish-event"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FLOW_EVENTS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
              <Button className="rounded-none" onClick={publish} disabled={busy} data-testid="flow-publish-confirm">
                {busy ? "Publishing…" : "Publish"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────── R43 · Action config inspector ───────────────

function FlowActionConfig({ selNode, updateSelected }) {
  const cfg = selNode.data?.config || {};
  const action = selNode.data?.action;
  const setC = (patch) => updateSelected({ config: { ...cfg, ...patch } });
  if (action === "webhook.post") {
    return (
      <>
        <Input placeholder="URL (https://…)" value={cfg.url || ""} onChange={e => setC({ url: e.target.value })} className="h-8 rounded-none text-xs font-mono" data-testid="flow-cfg-url" />
        <div className="text-[10px] text-muted-foreground">Use <code>${"${data.name}"}</code>-style tokens to inject the event payload.</div>
      </>
    );
  }
  if (action === "task.create") {
    return (
      <>
        <Input placeholder="Title" value={cfg.title || ""} onChange={e => setC({ title: e.target.value })} className="h-8 rounded-none text-xs" />
        <Input placeholder="Assignee user id (optional)" value={cfg.assignee_user_id || ""} onChange={e => setC({ assignee_user_id: e.target.value })} className="h-8 rounded-none text-xs font-mono" />
      </>
    );
  }
  if (action === "tag.add") {
    return <Input placeholder="Tag name" value={cfg.tag || ""} onChange={e => setC({ tag: e.target.value })} className="h-8 rounded-none text-xs" />;
  }
  if (action === "lead.assign") {
    return <Input placeholder="Assignee user id" value={cfg.assignee_user_id || ""} onChange={e => setC({ assignee_user_id: e.target.value })} className="h-8 rounded-none text-xs font-mono" />;
  }
  if (action === "lead.stage_set") {
    return <Input placeholder="Stage (e.g. warm)" value={cfg.stage || ""} onChange={e => setC({ stage: e.target.value })} className="h-8 rounded-none text-xs" />;
  }
  if (action === "wa.send_template") {
    return (
      <>
        <Input placeholder="Template name" value={cfg.template_name || ""} onChange={e => setC({ template_name: e.target.value })} className="h-8 rounded-none text-xs" />
        <div className="text-[10px] text-muted-foreground">Stubbed in this iteration — hooks into Interakt in a follow-up.</div>
      </>
    );
  }
  return null;
}

